const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

export interface ApiRequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public type: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiRequest(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<Response> {
  const {
    method = 'GET',
    headers = {},
    body
  } = options;

  const url = `${API_BASE_URL}${endpoint}`;
  
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers
  };

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    mode: 'cors',
    credentials: 'omit'
  };

  if (body && method !== 'GET') {
    config.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      // Parse response body once and preserve API detail text when present.
      let errorData: any = null;
      let errorText = '';
      try {
        errorText = await response.text();
        errorData = errorText ? JSON.parse(errorText) : null;
      } catch {
        errorData = null;
      }

      const detailMessage = errorData?.detail;
      const structuredMessage = errorData?.error?.message;
      const plainTextMessage = errorText && !errorData ? errorText : null;

      throw new ApiError(
        detailMessage || structuredMessage || plainTextMessage || `Request failed with status ${response.status}`,
        response.status,
        errorData?.error?.code || response.status.toString(),
        errorData?.error?.type || 'http_error'
      );
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Network or other errors
    throw new ApiError(
      'Network error or server unavailable',
      0,
      'NETWORK_ERROR',
      'network_error'
    );
  }
}

export async function apiGet<T>(endpoint: string, token?: string): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await apiRequest(endpoint, { headers });
  return response.json();
}

export async function apiPost<T>(
  endpoint: string,
  data: any,
  token?: string
): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await apiRequest(endpoint, {
    method: 'POST',
    headers,
    body: data
  });
  return response.json();
}

export async function apiPut<T>(
  endpoint: string,
  data: any,
  token?: string
): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await apiRequest(endpoint, {
    method: 'PUT',
    headers,
    body: data
  });
  return response.json();
}

export async function apiDelete(endpoint: string, token?: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  await apiRequest(endpoint, {
    method: 'DELETE',
    headers
  });
}
